import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import { Textarea } from '@/components/ui/textarea';
import { FormField } from '@/components/common/FormSection';
import { financeV2Service } from '../services/financeV2Service';
import { apiClient } from '../services/api-client';

import { getErrorMessage } from '../utils/error.utils'
interface CreateAnnualFeeModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function CreateAnnualFeeModal({ isOpen, onClose }: CreateAnnualFeeModalProps) {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    client_id: '',
    account_id: '',
    cost_center_id: '',
    category_id: '',
    year: new Date().getFullYear(),
    total_amount: '',
    installments: 12,
    issue_date: new Date().toISOString().split('T')[0],
    first_due_date: '',
    late_fee_percentage: '',
    discount_percentage: '',
    description: '',
    notes: '',
    apply_late_fee: false,
    generate_installments_now: true,
  });

  // Buscar clientes
  const { data: clientsData } = useQuery({
    queryKey: ['clients-for-annual-fee'],
    queryFn: async () => {
      const response = await apiClient.get('/api/clients', {
        params: { status: 'ACTIVE' }
      });
      return response.data;
    },
    enabled: isOpen,
  });

  // Buscar contas
  const { data: accountsData } = useQuery({
    queryKey: ['accounts-active'],
    queryFn: () => financeV2Service.accounts.list({ isActive: true }),
    enabled: isOpen,
  });

  // Buscar categorias
  const { data: categoriesData } = useQuery({
    queryKey: ['categories-active'],
    queryFn: () => financeV2Service.categories.list({}),
    enabled: isOpen,
  });

  // Buscar centros de custo
  const { data: costCentersData } = useQuery({
    queryKey: ['cost-centers-active'],
    queryFn: () => financeV2Service.costCenters.list({ isActive: true }),
    enabled: isOpen,
  });

  const createMutation = useMutation({
    mutationFn: (data: Parameters<typeof financeV2Service.annualFees.create>[0]) => financeV2Service.annualFees.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['annual-fees'] });
      queryClient.invalidateQueries({ queryKey: ['annual-fees-stats'] });
      alert('Anuidade criada com sucesso!');
      onClose();
      resetForm();
    },
    onError: (error: unknown) => {
      console.error('Erro ao criar anuidade:', error);
      alert(getErrorMessage(error, 'Erro ao criar anuidade. Verifique sua conexão.'));
    },
  });

  const resetForm = () => {
    setFormData({
      client_id: '',
      account_id: '',
      cost_center_id: '',
      category_id: '',
      year: new Date().getFullYear(),
      total_amount: '',
      installments: 12,
      issue_date: new Date().toISOString().split('T')[0],
      first_due_date: '',
      late_fee_percentage: '',
      discount_percentage: '',
      description: '',
      notes: '',
      apply_late_fee: false,
      generate_installments_now: true,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validação
    if (!formData.client_id || !formData.account_id || !formData.total_amount || !formData.first_due_date) {
      alert('Preencha todos os campos obrigatórios');
      return;
    }

    const payload: Parameters<typeof financeV2Service.annualFees.create>[0] = {
      client_id: formData.client_id,
      account_id: formData.account_id,
      year: formData.year,
      total_amount: parseFloat(formData.total_amount),
      installments: formData.installments,
      issue_date: formData.issue_date,
      first_due_date: formData.first_due_date,
      generate_installments: formData.generate_installments_now,
      ...(formData.cost_center_id && { cost_center_id: formData.cost_center_id }),
      ...(formData.category_id && { category_id: formData.category_id }),
      ...(formData.description && { description: formData.description }),
      ...(formData.notes && { notes: formData.notes }),
      ...(formData.apply_late_fee && formData.late_fee_percentage && {
        apply_late_fee: true,
        late_fee_percentage: parseFloat(formData.late_fee_percentage)
      }),
      ...(formData.discount_percentage && {
        discount_percentage: parseFloat(formData.discount_percentage)
      })
    };

    createMutation.mutate(payload);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  type ClientLite = { id: string; personalData?: { fullName?: string }; companyData?: { companyName?: string } }
  const clients = (clientsData?.data?.clients || []) as ClientLite[];
  type AccountLite = { id: string; name: string; type: string }
  type CategoryLite = { id: string; name: string }
  type CostCenterLite = { id: string; name: string }
  const accounts = (accountsData?.data?.accounts || []) as AccountLite[];
  const categories = (categoriesData?.data?.categories || []) as CategoryLite[];
  const costCenters = (costCentersData?.data?.cost_centers || []) as CostCenterLite[];

  const required = <span className="text-destructive">*</span>;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent size="lg" fullScreenOnMobile className="overflow-y-auto sm:max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>Nova Anuidade</DialogTitle>
          <DialogDescription>Contrato anual do cliente, cobrado em parcelas mensais.</DialogDescription>
        </DialogHeader>

        <form id="create-annual-fee-form" onSubmit={handleSubmit} className="flex min-w-0 flex-col gap-4">
          <FormField label={<>Cliente {required}</>} htmlFor="annual-fee-client">
            <NativeSelect
              id="annual-fee-client"
              name="client_id"
              value={formData.client_id}
              onChange={handleChange}
              required
            >
              <option value="">Selecione um cliente</option>
              {clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.personalData?.fullName || client.companyData?.companyName || 'Cliente sem nome'}
                </option>
              ))}
            </NativeSelect>
          </FormField>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField label={<>Ano {required}</>} htmlFor="annual-fee-year">
              <Input
                id="annual-fee-year"
                type="number"
                name="year"
                value={formData.year}
                onChange={handleChange}
                min="2020"
                max="2100"
                required
              />
            </FormField>
            <FormField label={<>Parcelas {required}</>} htmlFor="annual-fee-installments">
              <Input
                id="annual-fee-installments"
                type="number"
                name="installments"
                value={formData.installments}
                onChange={handleChange}
                min="1"
                max="12"
                required
              />
            </FormField>
          </div>

          <FormField label={<>Valor Total {required}</>} htmlFor="annual-fee-total">
            <Input
              id="annual-fee-total"
              type="number"
              name="total_amount"
              value={formData.total_amount}
              onChange={handleChange}
              step="0.01"
              min="0.01"
              required
              placeholder="0.00"
            />
          </FormField>

          <FormField label={<>Conta {required}</>} htmlFor="annual-fee-account">
            <NativeSelect
              id="annual-fee-account"
              name="account_id"
              value={formData.account_id}
              onChange={handleChange}
              required
            >
              <option value="">Selecione uma conta</option>
              {accounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.name} ({account.type})
                </option>
              ))}
            </NativeSelect>
          </FormField>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField label="Categoria" htmlFor="annual-fee-category">
              <NativeSelect
                id="annual-fee-category"
                name="category_id"
                value={formData.category_id}
                onChange={handleChange}
              >
                <option value="">Nenhuma</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </NativeSelect>
            </FormField>
            <FormField label="Centro de Custo" htmlFor="annual-fee-cost-center">
              <NativeSelect
                id="annual-fee-cost-center"
                name="cost_center_id"
                value={formData.cost_center_id}
                onChange={handleChange}
              >
                <option value="">Nenhum</option>
                {costCenters.map((cc) => (
                  <option key={cc.id} value={cc.id}>
                    {cc.name}
                  </option>
                ))}
              </NativeSelect>
            </FormField>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField label={<>Data de Emissão {required}</>} htmlFor="annual-fee-issue-date">
              <Input
                id="annual-fee-issue-date"
                type="date"
                name="issue_date"
                value={formData.issue_date}
                onChange={handleChange}
                required
              />
            </FormField>
            <FormField label={<>Primeiro Vencimento {required}</>} htmlFor="annual-fee-first-due-date">
              <Input
                id="annual-fee-first-due-date"
                type="date"
                name="first_due_date"
                value={formData.first_due_date}
                onChange={handleChange}
                required
              />
            </FormField>
          </div>

          {/* Multa e desconto: removidos da tela na v2 — eram gravados mas nunca
              aplicados às parcelas (RN-FIN-19). Ver DECISOES (fase 6). */}

          <FormField label="Descrição" htmlFor="annual-fee-description">
            <Input
              id="annual-fee-description"
              type="text"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Ex: Anuidade 2025"
            />
          </FormField>

          <FormField label="Observações" htmlFor="annual-fee-notes">
            <Textarea
              id="annual-fee-notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={3}
              placeholder="Observações adicionais..."
            />
          </FormField>

          <div className="flex items-center gap-2">
            <input
              id="annual-fee-generate-now"
              type="checkbox"
              name="generate_installments_now"
              checked={formData.generate_installments_now}
              onChange={handleChange}
              className="size-4 shrink-0 rounded-sm border-input accent-primary"
            />
            <Label htmlFor="annual-fee-generate-now" className="font-normal">
              Iniciar a cobrança agora (parcelas geradas mês a mês)
            </Label>
          </div>
        </form>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" form="create-annual-fee-form" loading={createMutation.isPending}>
            {createMutation.isPending ? 'Criando...' : 'Criar Anuidade'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
